import {UsuarioInfoTextField} from '@components/contas/UsuarioInformacao.tsx';
import {useAlert} from '@contexts/hooks/useAlert.ts';
import {useGeneral} from '@contexts/GeneralContext.tsx';
import {_Sitemap} from '@localization/siteMap/breadcrumbs/_Sitemap.ts';
import {_SolicitacoesEndpoints} from '@localization/siteMap/endpoints/_SolicitacoesEnpoints.ts';
import {DefaultInformationProps, DefaultNavLinkProps, DefaultNavTextFieldProps} from '@models/DefaultProps';
import {fullHeight, fullWidth} from '@models/helpers.ts';
import {ISolicitacaoTipo} from '@models/solicitacoes/solicitacoes.ts';
import {CheckOutlined, ClearOutlined, SaveOutlined} from '@mui/icons-material';
import {Button, Grid, TableCell, TableRow, TextField} from '@mui/material';
import {DateField} from '@mui/x-date-pickers';
import {NavigationLink} from '@shared/custom/NavigationLink.tsx';
import {TextFieldNav, TextFieldNavProps} from '@shared/custom/TextFieldNav.tsx';
import dayjs from 'dayjs';
import React, {useState} from 'react';
import {useApi} from "@contexts/hooks/api/useApi.ts";

export interface SolicitacaoTipoInformacaoProps extends DefaultInformationProps<ISolicitacaoTipo> {
	value: ISolicitacaoTipo;
	variant: 'list' | 'row' | 'navigationLink';
}

export const SolicitacaoTipoInformacao: React.FC<SolicitacaoTipoInformacaoProps> = (props) => {
	if (props.variant === 'list') return <SolicitacaoTipoInformacaoList {...props} />;
	if (props.variant === 'row') return <SolicitacaoTipoInformacaoRow {...props} />;
	if (props.variant === 'navigationLink') return <SolicitacaoTipoInformacaoNavLink {...props} />;
};

export const SolicitacaoTipoInformacaoGrid: React.FC<Omit<SolicitacaoTipoInformacaoProps, 'variant' | 'EndComponent'> & {
	readonly?: boolean, onUpdate?: (value: ISolicitacaoTipo) => void
}> = (props) => {
	const {value, readonly} = props;
	const [newValue, setNewValue] = useState<ISolicitacaoTipo>(value);
	const {Get} = useApi();
	const {adjustSizeLevel, pickCompare} = useGeneral();
	const {createAlertFromResponse} = useAlert();
	
	const handleUpdate = () => {
		Get(_SolicitacoesEndpoints.UpdateSolicitacaoTipo({...newValue})).then((x) => {
			if (x.status === 'Success') {
				if (props.onUpdate) props.onUpdate(x.items);
			}
			createAlertFromResponse(x);
		});
	};
	
	return (
		<Grid container spacing={1.5}>
			<Grid size={12}>
				<Grid container justifyContent={'space-between'}>
					<Grid>
					</Grid>
					<Grid>
						<Button endIcon={<SaveOutlined/>}
						        onClick={handleUpdate}
						        disabled={readonly || (pickCompare(value, newValue, ['id', 'nome', 'descricao', 'isAtivo']))}
						        variant={'outlined'}
						        color={'success'}>Salvar</Button>
					</Grid>
				</Grid>
			</Grid>
			<Grid size={9}>
				<TextField fullWidth value={newValue.nome}
				           onChange={(e) => setNewValue({...newValue, nome: e.target.value})}
				           disabled={readonly} label={'Nome'}/>
			</Grid>
			<Grid size={3}>
				<Button sx={{...fullHeight, ...fullWidth}}
				        disabled={readonly}
				        color={newValue.isAtivo ? 'success' : 'error'}
				        onClick={() => setNewValue({...newValue, isAtivo: !newValue.isAtivo})}
				        endIcon={newValue.isAtivo ? <CheckOutlined/> : <ClearOutlined/>}
				        variant={'outlined'}>
					{value.isAtivo ? 'Ativo' : 'Inativo'}
				</Button>
			</Grid>
			<Grid size={12}>
				<TextField maxRows={4} fullWidth value={newValue.descricao}
				           onChange={(e) => setNewValue({...newValue, nome: e.target.value})}
				           disabled={readonly} label={'Descrição'}/>
			</Grid>
			{value.usuarioCriacao && <Grid size={adjustSizeLevel({micro: 12, md: 6})}>{value.usuarioCriacao &&
                <UsuarioInfoTextField label={'Criado por'} value={value.usuarioCriacao}/>}
            </Grid>}
			<Grid size={adjustSizeLevel({micro: 12, md: 6})}><DateField fullWidth label={'Criado em'} disabled
			                                                            value={dayjs(value.dataCriacao)}/></Grid>
		
		</Grid>
	);
};

export const SolicitacaoTipoInformacaoTextField: React.FC<Omit<SolicitacaoTipoInformacaoProps, 'variant' | 'EndComponent'> & DefaultNavTextFieldProps<ISolicitacaoTipo, keyof ISolicitacaoTipo, keyof  ISolicitacaoTipo>> = (props) => {
	const {value, slotProps, label} = props;
	const {solicitacoes} = _Sitemap;
	
	const p: TextFieldNavProps<ISolicitacaoTipo> = {
		label: label ?? 'Tipo de Solicitação',
		slotProps: {...slotProps},
		displayMode: props.displayMode,
		tooltip: props.tooltip,
		value: value,
		pathName: solicitacoes.children.tipos.children.detalhes.fullUrl,
		searchData: {
			solicitacaoTipoId: props.value.id,
		},
	};
	
	return (
		<TextFieldNav {...p}/>
	);
};

export function SolicitacaoTipoInformacaoList (props: Omit<SolicitacaoTipoInformacaoProps, 'variant'>) {
	const {value, EndComponent} = props;
	
	return (
		<Grid container alignItems={'center'} justifyContent={'space-between'}>
			<Grid container spacing={1}>
				<Grid><SolicitacaoTipoInformacaoNavLink {...props}/></Grid>
				<Grid>{value.descricao}</Grid>
				<Grid>{value.isAtivo ? <CheckOutlined fontSize={'small'}/> : <ClearOutlined fontSize={'small'}/>}</Grid>
			</Grid>
			{EndComponent && <Grid><EndComponent {...props.value}/></Grid>}
		</Grid>
	);
}

export const SolicitacaoTipoInformacaoNavLink: React.FC<Omit<SolicitacaoTipoInformacaoProps, 'variant'> & DefaultNavLinkProps<ISolicitacaoTipo>> = (props) => {
	const {solicitacoes} = _Sitemap;
	
	return (
		<NavigationLink pathName={solicitacoes.children.tipos.children.detalhes.fullUrl} searchData={{
			solicitacaoTipoId: props.value.id,
		}} label={props.value.nome}
		                onClick={props.onClick}
		                chipSx={{...props.slotProps?.chip?.sx}}
		                tableMode={props.slotProps?.chip?.tableMode}
		/>
	);
};

export const SolicitacaoTipoInformacaoRow: React.FC<Omit<SolicitacaoTipoInformacaoProps, 'variant'>> = (props) => {
	const {value, EndComponent} = props;
	
	return (
		<TableRow>
			<TableCell>{value.id}</TableCell>
			<TableCell>
				<SolicitacaoTipoInformacaoNavLink slotProps={{chip: {tableMode: true}}} {...props} />
			</TableCell>
			<TableCell>{value.descricao}</TableCell>
			<TableCell align={'right'}>{value.isAtivo ? <CheckOutlined fontSize={'small'}/> :
				<ClearOutlined fontSize={'small'}/>}</TableCell>
			<TableCell align={'right'}>{EndComponent &&
                <EndComponent {...value}/>
			}</TableCell>
		</TableRow>
	);
};